import { COSTA_RICAN_PROVINCES } from './constants'
import { LocaleConfig } from '@/config/types'
import { CostaRicanAddress } from '@/config/cr/types'
import { Country } from '@/i18n/routing'
import { safeStringTrim } from '@/utils/safe-trim'

export const crConfig: LocaleConfig = {
  name: 'Costa Rica',
  country: 'CR',
  coordinates: {
    lat: 9.7489,
    lng: -83.7534
  },
  timezone: 'America/Costa_Rica',
  phoneFormat: '+506 XXXX-XXXX',
  dateFormat: 'dd/MM/yyyy',
  flag: '/costarica.png',
  mainPoliticalDivisions: COSTA_RICAN_PROVINCES,
  zoomLevels: {
    country: 8,
    veryLarge: 9,
    large: 10,
    medium: 11,
    small: 12,
    verySmall: 14,
    default: 8
  },
  radiusConfig: {
    country: 25000, // Smaller radius for Costa Rica's compact size
    administrativeArea: 15000,
    locality: 5000,
    neighborhood: 2000,
    route: 1000,
    intersection: 200,
    premise: 100,
    pointOfInterest: 500
  },
  addressConfig: {
    mainPoliticalDivisionField: 'province',
    country: Country.CR,
    formatter: (data: CostaRicanAddress) => {
      const parts: string[] = []

      // Build the street part
      const streetName = safeStringTrim(data.streetName)
      const streetNumber = safeStringTrim(data.streetNumber)

      if (streetName && streetNumber) {
        parts.push(`${streetName} ${streetNumber}`)
      } else if (streetName) {
        parts.push(streetName)
      } else if (streetNumber) {
        parts.push(`#${streetNumber}`)
      }

      // Additional information (building, apartment, etc.)
      const additionalInfo = safeStringTrim(data.additionalInfo)
      if (additionalInfo) {
        parts.push(additionalInfo)
      }

      // District
      const district = safeStringTrim(data.district)
      if (district) {
        parts.push(district)
      }

      // Canton
      const canton = safeStringTrim(data.canton)
      if (canton) {
        parts.push(canton)
      }

      // Province
      const province = safeStringTrim(data.province)
      if (province) {
        parts.push(province)
      }

      // Postal code
      const postalCode = safeStringTrim(data.postalCode)
      if (postalCode) {
        parts.push(postalCode)
      }

      return parts.filter(Boolean).join(', ')
    },

    parser: (legacyData: any) => {

      const street = safeStringTrim(legacyData.street)
      let streetName = ''
      let streetNumber = ''

      if (street) {
        // Try to separate street name from number
        const words = street.split(' ')
        const lastWord = words[words.length - 1]

        if (/^\d+[A-Za-z]*$/.test(lastWord)) {
          streetName = words.slice(0, -1).join(' ')
          streetNumber = lastWord
        } else {
          streetName = street
          streetNumber = ''
        }
      }

      const result: CostaRicanAddress = {
        streetName: streetName,
        streetNumber: streetNumber,
        additionalInfo: '',
        district: safeStringTrim(legacyData.neighborhood),
        canton: safeStringTrim(legacyData.city),
        province: safeStringTrim(legacyData.state),
        postalCode: safeStringTrim(legacyData.postalCode)
      }

      return result
    },

    validator: (data: CostaRicanAddress) => {
      const errors: string[] = []

      // Required fields
      const canton = safeStringTrim(data.canton)
      if (!canton) {
        errors.push('Cantón es requerido')
      }

      const province = safeStringTrim(data.province)
      if (!province) {
        errors.push('Provincia es requerida')
      }

      // Postal code validation (Costa Rica uses 5-digit postal codes)
      const postalCode = safeStringTrim(data.postalCode)
      if (postalCode && !/^\d{5}$/.test(postalCode)) {
        errors.push('Código postal debe tener 5 dígitos')
      }

      return { valid: errors.length === 0, errors }
    },

    searchableFields: [
      { key: 'district', label: 'Distrito', mongoPath: 'address.data.district' },
      { key: 'canton', label: 'Cantón', mongoPath: 'address.data.canton' },
      { key: 'province', label: 'Provincia', mongoPath: 'address.data.province' },
      { key: 'postalCode', label: 'Código Postal', mongoPath: 'address.data.postalCode' }
    ],

    excelMapping: {
      streetName: {
        label: 'Calle/Avenida',
        dbField: 'address.data.streetName'
      },
      streetNumber: {
        label: 'Número',
        dbField: 'address.data.streetNumber'
      },
      additionalInfo: {
        label: 'Info Adicional',
        dbField: 'address.data.additionalInfo'
      },
      district: {
        label: 'Distrito',
        dbField: 'address.data.district'
      },
      canton: {
        label: 'Cantón',
        dbField: 'address.data.canton'
      },
      province: {
        label: 'Provincia',
        dbField: 'address.data.province'
      },
      postalCode: {
        label: 'Código Postal',
        dbField: 'address.data.postalCode'
      }
    }
  }
}